import { ValidationState, FormData } from "../types";
import { PhoneInput } from "../components/PhoneInput";

interface InitialFormStageProps {
  formData: FormData;
  validation: ValidationState;
  isLoading: boolean;
  title: string;
  subtitle: string;
  onSubmit: (e: Event) => void;
  onInputChange: (e: Event) => void;
}

export const InitialFormStage = ({
  formData,
  validation,
  isLoading,
  title,
  subtitle,
  onSubmit,
  onInputChange,
}: InitialFormStageProps) => {
  // Helper function to create a synthetic event for the phone input
  const handlePhoneChange = (value: string) => {
    const syntheticEvent = {
      target: {
        name: "phone",
        value,
      },
    } as unknown as Event;
    onInputChange(syntheticEvent);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <h1 className="kismet-form-title text-4xl font-bold text-center mb-4">
        {title}
      </h1>

      <p className="kismet-form-subtitle text-xl text-gray-600 text-center mb-8">
        {subtitle}
      </p>

      <div className="grid grid-cols-2 gap-4">
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={onInputChange}
          placeholder="First"
          className="kismet-form-input border-b border-gray-300 p-2 focus:outline-none focus:border-black transition-colors"
          disabled={isLoading}
        />
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={onInputChange}
          placeholder="Last"
          className="kismet-form-input border-b border-gray-300 p-2 focus:outline-none focus:border-black transition-colors"
          disabled={isLoading}
        />
      </div>

      <div className="space-y-1">
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={onInputChange}
          placeholder="Email"
          className={`kismet-form-input w-full border-b p-2 focus:outline-none transition-colors ${
            validation.email.isDirty
              ? validation.email.isValid
                ? "border-green-500 focus:border-green-700"
                : "border-red-500 focus:border-red-700"
              : "border-gray-300 focus:border-black"
          }`}
          disabled={isLoading}
        />
        {validation.email.isDirty && !validation.email.isValid && (
          <p className="text-red-600 text-sm mt-1">
            {validation.email.message}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <PhoneInput
          value={formData.phone}
          onChange={handlePhoneChange}
          isValid={validation.phone.isValid}
          isDirty={validation.phone.isDirty}
          disabled={isLoading}
          className="kismet-form-input"
          errorMessage={validation.phone.message}
        />
      </div>

      <div className="flex items-start space-x-3 py-2">
        <input
          type="checkbox"
          name="marketingConsent"
          checked={formData.marketingConsent}
          onChange={onInputChange}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
          disabled={isLoading}
        />
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">
            I agree to receive marketing communications via email and phone
            about deals and updates.
          </label>
          {!validation.marketingConsent?.isValid && (
            <p className="text-red-600 text-sm mt-1">
              {validation.marketingConsent.message}
            </p>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full py-3 rounded-full text-base font-medium transition-colors ${
          isLoading
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-black text-white hover:bg-gray-800"
        }`}
      >
        {isLoading ? "Submitting..." : "Continue"}
      </button>
    </form>
  );
};
